// @ts-nocheck
import { DialogContentText, Grid } from '@mui/material';
import { BasicButton, SubmitButton } from '@tassat/button';
import { AccountType } from '@tassat/common-definitions';
import {
    CheckboxField,
    SelectField,
    SimpleErrorMessage,
    TextField
} from '@tassat/formik-input';
import { CustomerModifyFirmAccountReq } from '@tassat/fred-definitions';
import { WalletRecordExtended, WalletRecordMapped } from '@tassat/fred-extended-definitions';
import { ActionTypes, SelectOption, doesWorkflowRejectCodeExist } from '@tassat/ui-definitions';
import {
    ConfirmationWorkflowAction,
    ConfirmationWorkflowConsumer,
    ConfirmationWorkflowContext,
    ConfirmationWorkflowReducer,
    ConfirmationWorkflowState,
    DEFAULT_INITIAL_STATE
} from '@tassat/workflow';
import React, { FC, useMemo } from 'react';

import { muiStyles } from './styles';
import { IFormProps } from './types';

export const WalletFormContents: FC<IFormProps> = (props) => {
    const {
        dirty,
        formProps,
        isSubmitting,
        isValid,
        errors,
        submitError,
        submitForm,
        values
    } = props;

    const {
        accountSelectOptions,
        acgSelectOptions,
        actionType,
        firm,
        firmWallets,
        accountRelationships
    } = formProps;

    const classes = muiStyles(props);
    const editWallet = actionType === ActionTypes.edit && formProps.editWallet || undefined;
    const linkedAccountRecord = actionType === ActionTypes.edit && formProps.linkedAccountRecord || undefined;

    const primaryWalletExists = React.useMemo(() => {
        const checkForPrimaryWallets = (selectedAccountId) => {
            const relatedAccounts = Object.keys(accountRelationships)
                .map(id => accountRelationships[id])
                .filter(relationship => relationship.AccountId1 === selectedAccountId || relationship.AccountId2 === selectedAccountId);
            const relatedWallets = relatedAccounts.map(relation => {
                return firmWallets.find(wallet => wallet.AccountId === relation.AccountId2 || wallet.AccountId === relation.AccountId1);
            });

            return relatedWallets.some(wallet => wallet.IsPrimary);
        };

        if (actionType === ActionTypes.add && values.LinkedAccount.value !== '') {
            return checkForPrimaryWallets(values.LinkedAccount.value);
        }

        if (actionType === ActionTypes.edit && linkedAccountRecord) {
            return checkForPrimaryWallets(linkedAccountRecord[0]?.AccountId);
        }

        return false;
    }, [ actionType, values.LinkedAccount, accountRelationships, firmWallets, linkedAccountRecord ]);

    React.useMemo(() => {
        if (actionType === ActionTypes.add) {
            if (values.LinkedAccount.value !== '' && !primaryWalletExists) {
                values.IsPrimary = true;
            } else {
                values.IsPrimary = false;
            }
        }

        return;
    }, [ actionType, values, primaryWalletExists ]);

    const accessControlOptions: SelectOption[] = [
        {
            label: 'Yes',
            value: true
        },
        {
            label: 'No',
            value: false
        }
    ];

    const [
        confirmationWorkflowState,
        dispatchConfirmationWorkflowAction
    ] = React.useReducer<React.Reducer<
        ConfirmationWorkflowState,
        ConfirmationWorkflowAction
    >>(ConfirmationWorkflowReducer, DEFAULT_INITIAL_STATE);
    const confirmationContext = React.useMemo(() => ({
        confirmationWorkflowState,
        dispatchConfirmationWorkflowAction
    }), [ confirmationWorkflowState, dispatchConfirmationWorkflowAction ]);

    const closeAccountValidation = async (wallet: WalletRecordMapped): Promise<boolean> => {
        let validationError = '';

        wallet.walletStats?.forEach((walletStat) => {
            if (walletStat.AvailablePosition && parseFloat(walletStat.AvailablePosition) > 0) {
                validationError = 'Cannot close an account with positive balance';
            }
        });

        if (wallet.wallet?.IsLiquidityFacility) {
            validationError = 'Unable to close liquidity facility account';
        } else if (wallet.wallet?.IsPrimary) {
            validationError = 'Unable to close primary account';
        } else {
            // make history request to check on open transactions
            if (actionType !== ActionTypes.edit) {
                return true;
            }
            try {
                if (wallet.wallet?.Account) {
                    const response = await formProps.getTransactions({
                        Condition: `isCompleted|EQ|false:wallet|EQ|${wallet.wallet?.Account}`,
                        Sort: 'auditTime|DESC',
                        PageNo: 1,
                        RecordPerPage: 4
                    });
                    if (response.data && response.data.TotalCount > 0) {
                        validationError = 'Cannot close an account with open transactions';
                    }
                }
            } catch (error) {
                validationError = 'Could not validate if there are open transactions, please try again';
            }
        }

        if (validationError) {
            const validationErrorAction: ConfirmationWorkflowAction = {
                type: 'VALIDATION_FAILED',
                workflowConfiguration: {
                    validationTitle: `Failed to Close Account ${wallet.wallet?.AccountName}`,
                    validationMessage: validationError
                }
            };
            dispatchConfirmationWorkflowAction(validationErrorAction);

            return true;
        }

        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: wallet.wallet,
                actionType: ActionTypes.close,
                confirmTitle: `Confirm Closing Account ${wallet.wallet?.AccountName}`,
                confirmBody: `Please confirm closing the account ${wallet.wallet?.AccountName}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const closeAccount = async (wallet: WalletRecordExtended) => {
        if (actionType !== ActionTypes.edit) {
            return;
        }
        try {
            let response = { data: undefined };
            const [
                ACGId1,
                ACGId2,
                ACGId3
            ] = values.ACGMapping.map((acg) => acg.value as string);
            const sendValues: Omit<
                CustomerModifyFirmAccountReq,
                'RequestId' |
                'RequesterId' |
                'FirmId' |
                'AccountId' |
                'Account' |
                'AccountName1' |
                'AccountName2' |
                'ShortName' |
                'WalletName1' |
                'WalletName2' |
                'UUID'
            > & { LinkedAccountId: string } = {
                AccountType: AccountType.CLIENT_EOA,
                AccountName: wallet.AccountName,
                AccessControl: Boolean(values.AccessControl.value),
                LinkedAccountId: linkedAccountRecord ? linkedAccountRecord[0].AccountId : '',
                IsLiquidityFacility: wallet.IsLiquidityFacility,
                Email: wallet.Email,
                Notes: wallet.Notes,
                ACGId1: ACGId1 || '0',
                ACGId2: ACGId2 || '0',
                ACGId3: ACGId3 || '0',
                IsWire: wallet.IsWire,
                IsLocked: wallet.IsLocked,
                IsClosed: true,
                IsPrimary: wallet.IsPrimary
            };

            response = await formProps.updateFirmAccount(wallet.FirmId, wallet.AccountId, sendValues);

            if (response.data && response.data?.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Closed Account ${wallet.AccountName}`,
                        successBody: 'Successfully closed the account ' +
                            `${wallet.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Close Account ${wallet.AccountName}`,
                        failureBody: `Could not close account ${wallet.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Close Account ${wallet.AccountName}`,
                    failureBody: 'Something went wrong closing the account ' +
                        `${wallet.AccountName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    const reopenAccountValidation = (wallet: WalletRecordMapped): boolean => {
        const validationSuccessfulAction: ConfirmationWorkflowAction = {
            type: 'VALIDATION_SUCCESS',
            workflowConfiguration: {
                selectedItem: wallet.wallet,
                actionType: ActionTypes.reopen,
                confirmTitle: `Confirm Reopening Account ${wallet.wallet.AccountName}`,
                confirmBody: 'Please confirm reopening the account ' +
                    `${wallet.wallet.AccountName}.`
            }
        };
        dispatchConfirmationWorkflowAction(validationSuccessfulAction);

        return false;
    };

    const reopenAccount = async (wallet: WalletRecordExtended) => {
        if (actionType !== ActionTypes.edit) {
            return;
        }
        try {
            let response = { data: undefined };
            const [
                ACGId1,
                ACGId2,
                ACGId3
            ] = values.ACGMapping.map((acg) => acg.value as string);
            const sendValues: Omit<
                CustomerModifyFirmAccountReq,
                'RequestId' |
                'RequesterId' |
                'FirmId' |
                'AccountId' |
                'Account' |
                'AccountName1' |
                'AccountName2' |
                'ShortName' |
                'WalletName1' |
                'WalletName2' |
                'UUID'
            > & { LinkedAccountId: string } = {
                AccountType: AccountType.CLIENT_EOA,
                AccountName: wallet.AccountName,
                AccessControl: Boolean(values.AccessControl.value),
                LinkedAccountId: linkedAccountRecord ? linkedAccountRecord[0].AccountId : '',
                IsLiquidityFacility: wallet.IsLiquidityFacility,
                Email: wallet.Email,
                Notes: wallet.Notes,
                ACGId1: ACGId1 || '0',
                ACGId2: ACGId2 || '0',
                ACGId3: ACGId3 || '0',
                IsWire: wallet.IsWire,
                IsLocked: wallet.IsLocked,
                IsClosed: false,
                IsPrimary: wallet.IsPrimary
            };

            response = await formProps.updateFirmAccount(wallet.FirmId, wallet.AccountId, sendValues);

            if (response.data && response.data?.Status === 48) {
                const serverSuccessAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_SUCCESS',
                    workflowConfiguration: {
                        successTitle: `Successfully Reopened Account ${wallet.AccountName}`,
                        successBody: 'Successfully reopened the account ' +
                            `${wallet.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverSuccessAction);
            } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Reopen Account ${wallet.AccountName}`,
                        failureBody: `Could not reopen account ${wallet.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            } else {
                const serverErrorAction: ConfirmationWorkflowAction = {
                    type: 'SERVER_ERROR',
                    workflowConfiguration: {
                        failureTitle: `Failed to Reopen Account ${wallet.AccountName}`,
                        failureBody: `Could not reopen account ${wallet.AccountName}.`
                    }
                };
                dispatchConfirmationWorkflowAction(serverErrorAction);
            }
        } catch (error) {
            const serverErrorAction: ConfirmationWorkflowAction = {
                type: 'SERVER_ERROR',
                workflowConfiguration: {
                    failureTitle: `Failed to Reopen Account ${wallet.AccountName}`,
                    failureBody: 'Something went wrong reopening the account ' +
                        `${wallet.AccountName}.`
                }
            };
            dispatchConfirmationWorkflowAction(serverErrorAction);
        }
    };

    React.useEffect(() => {
        // set initial confirmation settings
        const configureConfirmationWorkflowAction: ConfirmationWorkflowAction = {
            type: 'CONFIGURE_WORKFLOW',
            workflowConfiguration: {
                selectedItem: editWallet,
                setSelectedItem: () => editWallet,
                preActionValidationAndConfig: {
                    [ActionTypes.close]: closeAccountValidation,
                    [ActionTypes.reopen]: reopenAccountValidation
                },
                onConfirm: {
                    [ActionTypes.close]: closeAccount,
                    [ActionTypes.reopen]: reopenAccount
                }
            }
        };
        dispatchConfirmationWorkflowAction(configureConfirmationWorkflowAction);
    }, []);

    const renderButton = () => {
        if (actionType === ActionTypes.add || (actionType === ActionTypes.edit && !editWallet)) {
            return null;
        }

        return (
            actionType === ActionTypes.edit && !editWallet?.wallet.IsClosed ? (
                <Grid item>
                    <BasicButton
                        onClick={() => editWallet && closeAccountValidation(editWallet)}
                        text='Close Account'
                        type='action'
                    />
                </Grid>
            ) : (
                <Grid item>
                    <BasicButton
                        onClick={() => editWallet && reopenAccountValidation(editWallet)}
                        text='Reopen Account'
                        type='action'
                    />
                </Grid>
            )
        );
    };

    const doesLiquidityFacilityWalletExist = useMemo(() => {
        return firmWallets.some((wallet) => wallet.IsLiquidityFacility);
    }, [firmWallets]);

    return (
        <ConfirmationWorkflowContext.Provider value={confirmationContext}>
            <form className={classes.flexColumn}>
                <Grid
                    container
                    direction='column'
                    spacing={0}
                >
                    <Grid
                        className={classes.fullWidth}
                        container
                        item
                        spacing={2}
                        xs={12}
                    >
                        <Grid
                            item
                            xs={6}
                        >
                            <TextField
                                disabled={editWallet?.wallet.IsClosed}
                                errors={errors}
                                id='WalletForm_AccountName'
                                label='Account Name'
                                maxLength={64}
                                name='AccountName'
                                placeholder='Account Name'
                                values={values}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                        >
                            <SelectField
                                autoComplete
                                disabled={editWallet?.wallet.IsClosed || (linkedAccountRecord ? true : false)}
                                errors={errors}
                                id='WalletForm_LinkedAccount'
                                label='Linked BD Account Number'
                                name='LinkedAccount'
                                options={accountSelectOptions}
                                placeholder='Linked BD Account Number'
                                values={values}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                        >
                            <SelectField
                                autoComplete
                                disabled={editWallet?.wallet.IsClosed}
                                errors={errors}
                                hasAllOption
                                id='WalletForm_ACGMapping'
                                label='ACG Number'
                                multiSelect
                                name='ACGMapping'
                                options={acgSelectOptions}
                                placeholder='Account Client Group'
                                values={values}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                        >
                            <TextField
                                disabled={editWallet?.wallet.IsClosed}
                                errors={errors}
                                id='WalletForm_Email'
                                label='Notification Email'
                                maxLength={64}
                                name='Email'
                                placeholder='Notification Email'
                                values={values}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={6}
                        >
                            <TextField
                                disabled={editWallet?.wallet.IsClosed}
                                errors={errors}
                                id='WalletForm_Notes'
                                label='Notes'
                                maxLength={50}
                                name='Notes'
                                placeholder='Notes'
                                values={values}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={2}
                        >
                            <SelectField
                                autoComplete
                                disabled={editWallet?.wallet.IsClosed}
                                errors={errors}
                                id='WalletForm_AccessControl'
                                label='Access Control'
                                name='AccessControl'
                                options={accessControlOptions}
                                values={values}
                            />
                        </Grid>
                        {firm.IsLiquidityFacility && (
                            <Grid
                                className={classes.checkboxField}
                                item
                            >
                                <CheckboxField
                                    disabled={editWallet?.wallet.IsClosed ||
                                        (doesLiquidityFacilityWalletExist && !values.IsLiquidityFacility)}
                                    errors={errors}
                                    label='Liquidity Facility'
                                    name='IsLiquidityFacility'
                                    values={values}
                                />
                            </Grid>
                        )}
                        {values.LinkedAccount.value !== '' &&
                            <Grid
                                className={classes.checkboxField}
                                item
                            >
                                <CheckboxField
                                    disabled={editWallet?.wallet.IsClosed || editWallet?.wallet.IsPrimary || (values.LinkedAccount.value !== '' && !primaryWalletExists)}
                                    errors={errors}
                                    label='Primary Account'
                                    name='IsPrimary'
                                    values={values}
                                />
                                {values.LinkedAccount.value !== '' && primaryWalletExists &&
                                    <DialogContentText
                                        className={classes.warningStyle}
                                        noWrap={false}
                                    >
                                        Clicking this checkbox will make this account the new primary account for the linked BD account.
                                    </DialogContentText>}
                            </Grid>}
                    </Grid>
                    <Grid
                        alignItems='center'
                        className={classes.submitButtonContainer}
                        container
                        item
                        justifyContent='space-evenly'
                        xs={12}
                    >
                        {renderButton()}
                        <Grid
                            className={classes.submitButton}
                            container
                            direction='column'
                            item
                        >
                            <SubmitButton
                                disabled={!dirty ||
                                    isSubmitting ||
                                    !isValid ||
                                    editWallet?.wallet.IsClosed}
                                onClick={submitForm}
                                text={actionType === ActionTypes.edit ? 'Save Changes' : 'Create Account'}
                                type='submit'
                            />
                            <SimpleErrorMessage
                                className={classes.submitError}
                                error={submitError}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </form>
            <ConfirmationWorkflowConsumer />
        </ConfirmationWorkflowContext.Provider>
    );
};